// src/components/kit/epk/EpkRenderer.tsx
//
// Core rendering component for EPK pages. Takes a PublicEpkData or
// EpkFormState and renders the complete EPK with the chosen layout,
// colors, fonts, and content.
//
// All generated CSS is scoped to the .epk-root container so that styles
// do not leak into the parent application when used in the builder preview.
// CSS adapted from the EPK mockup studio.

import { useState, useEffect } from 'react';
import type { PublicEpkData, EpkFormState } from '@/types/epk';
import { EPK_FONT_THEMES } from './themes';
import type { EpkFontThemeId } from '@/types/epk';

const SOCIAL_ICON_SVGS: Record<string, React.ReactNode> = {
  instagram: (
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  ),
  facebook: (
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  ),
  tiktok: (
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  ),
  youtube: (
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  ),
  bandcamp: (
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm3.5 15.09H5.472l3.028-6.18h10.028l-3.028 6.18z" />
    </svg>
  ),
  twitter: (
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  spotify: (
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
    </svg>
  ),
  'apple-music': (
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043A5.022 5.022 0 0019.7.165a10.104 10.104 0 00-1.87-.163L17.203 0H6.796L6.17.002a10.104 10.104 0 00-1.87.163 5.022 5.022 0 00-1.874.726C1.308 1.624.563 2.624.246 3.934a9.23 9.23 0 00-.24 2.19L0 6.75v10.5l.006.627a9.23 9.23 0 00.24 2.19c.317 1.31 1.062 2.31 2.18 3.043a5.022 5.022 0 001.874.726c.613.12 1.24.164 1.87.163L6.796 24h10.407l.627-.002a10.104 10.104 0 001.87-.163 5.022 5.022 0 001.874-.726c1.118-.733 1.863-1.733 2.18-3.043a9.23 9.23 0 00.24-2.19L24 17.25V6.75l-.006-.627zM17.994 10.5v5.25a2.25 2.25 0 01-2.25 2.25 2.25 2.25 0 010-4.5c.414 0 .81.112 1.148.316l.002-.001V9.75l-6 1.5v5.25a2.25 2.25 0 01-2.25 2.25 2.25 2.25 0 010-4.5c.414 0 .81.112 1.148.316l.002-.001V8.25l8-2z" />
    </svg>
  ),
  soundcloud: (
    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
      <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.05-.1-.1-.1m-.899.828c-.06 0-.091.037-.104.094L0 14.479l.172 1.308c.013.06.045.094.104.094.057 0 .09-.037.104-.094l.194-1.308-.194-1.332c-.014-.057-.047-.094-.104-.094m1.8-.6c-.067 0-.12.06-.12.12l-.194 2.1.194 2.04c0 .067.053.12.12.12.067 0 .12-.053.12-.12l.217-2.04-.217-2.1c0-.06-.053-.12-.12-.12m.9-.12c-.074 0-.127.06-.127.127l-.18 2.34.18 2.1c0 .074.053.127.127.127.067 0 .127-.053.127-.127l.2-2.1-.2-2.34c0-.067-.06-.127-.127-.127m.932-.225c-.08 0-.142.067-.142.142l-.165 2.685.165 2.145c0 .08.06.142.142.142.075 0 .135-.06.142-.142l.187-2.145-.187-2.685c-.007-.075-.067-.142-.142-.142m.905-.09c-.09 0-.157.074-.157.157l-.15 2.775.15 2.182c0 .09.067.157.157.157.082 0 .15-.067.157-.157l.172-2.182-.172-2.775c-.007-.082-.075-.157-.157-.157m.967-.08c-.097 0-.172.08-.172.172l-.142 2.855.142 2.19c0 .097.075.172.172.172.09 0 .165-.075.172-.172l.157-2.19-.157-2.855c-.007-.09-.082-.172-.172-.172m.96-.225c-.104 0-.187.09-.187.187l-.127 3.08.127 2.22c0 .105.082.187.187.187.097 0 .18-.082.187-.187l.142-2.22-.142-3.08c-.007-.097-.09-.187-.187-.187m1.065-.203c-.112 0-.202.09-.202.202l-.12 3.285.12 2.235c0 .112.09.202.202.202.105 0 .195-.09.202-.202l.135-2.235-.135-3.285c-.007-.112-.097-.202-.202-.202m.96 0c-.12 0-.21.097-.21.21l-.113 3.285.113 2.235c.007.12.09.21.21.21.112 0 .202-.09.21-.21l.127-2.235-.127-3.285c-.008-.113-.098-.21-.21-.21m1.013-.12c-.127 0-.225.105-.225.225l-.105 3.405.105 2.25c.007.127.097.225.225.225.12 0 .217-.098.225-.225l.12-2.25-.12-3.405c-.008-.12-.105-.225-.225-.225m1.065.18c-.135 0-.24.112-.24.24l-.098 3.225.098 2.235c.008.135.105.24.24.24.127 0 .232-.105.24-.24l.105-2.235-.105-3.225c-.008-.128-.113-.24-.24-.24m1.012-.42c-.142 0-.255.12-.255.255l-.082 3.645.082 2.235c.008.142.113.255.255.255.135 0 .247-.113.255-.255l.09-2.235-.09-3.645c-.008-.135-.12-.255-.255-.255m1.155-.165c-.15 0-.27.127-.27.27l-.075 3.81.075 2.227c.007.15.12.27.27.27.142 0 .262-.12.27-.27l.082-2.227-.082-3.81c-.008-.143-.128-.27-.27-.27m1.08-.39c-.165 0-.293.135-.293.293l-.067 4.2.067 2.22c.008.157.128.285.293.285.15 0 .278-.128.285-.285l.075-2.22-.075-4.2c-.007-.158-.135-.293-.285-.293m1.11-.42c-.172 0-.308.142-.308.308l-.06 4.62.06 2.205c.007.165.135.3.308.3.165 0 .3-.135.307-.3l.068-2.205-.068-4.62c-.007-.166-.142-.308-.307-.308m2.267 1.17c-.18 0-.33.15-.33.33l-.045 3.87.045 2.19c.008.18.15.33.33.33a.332.332 0 00.33-.33l.052-2.19-.052-3.87a.336.336 0 00-.33-.33m-1.065-.42c-.18 0-.322.15-.322.322l-.053 4.29.053 2.197c.008.18.142.322.322.322.172 0 .315-.142.322-.322l.06-2.197-.06-4.29c-.007-.172-.15-.322-.322-.322m2.198.63a.35.35 0 00-.345.345l-.037 3.66.037 2.175a.35.35 0 00.345.345.35.35 0 00.345-.345l.037-2.175-.037-3.66a.35.35 0 00-.345-.345m1.095.81c-.203 0-.36.165-.36.36l-.03 2.85.03 2.145c.008.195.158.36.36.36.195 0 .352-.165.36-.36l.03-2.145-.03-2.85c-.008-.195-.165-.36-.36-.36m1.11-.12c-.21 0-.375.172-.375.375l-.022 2.97.022 2.13c.008.21.165.375.375.375.202 0 .367-.165.375-.375l.022-2.13-.022-2.97c-.008-.203-.173-.375-.375-.375m1.11.48c-.21 0-.382.18-.382.382l-.015 2.49.015 2.1c.008.21.172.382.382.382.203 0 .375-.172.383-.382l.015-2.1-.015-2.49c-.008-.202-.18-.382-.383-.382" />
    </svg>
  ),
  website: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
    </svg>
  ),
  other: (
    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  ),
};

interface EpkRendererProps {
  data: PublicEpkData | EpkFormState;
  onLinkClick?: (linkId: string, label: string) => void;
  onEmailSubmit?: (email: string) => Promise<void>;
  isPreview?: boolean;
  previewMode?: 'mobile' | 'desktop';
}

// ---- Helpers ----

function buildGoogleFontsUrl(displayFont: string, bodyFont: string): string {
  const fonts = new Set([displayFont, bodyFont]);
  const families = [...fonts]
    .map((f) => {
      const slug = f.replace(/ /g, '+');
      return `family=${slug}:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500`;
    })
    .join('&');
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

function formatTourDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function getFontThemeVars(data: PublicEpkData | EpkFormState): { displayStyle: string; displayWeight: string; displayTracking: string } {
  const themeId = data.font_theme_id as Exclude<EpkFontThemeId, 'custom'>;
  const theme = EPK_FONT_THEMES[themeId];
  if (theme) {
    return { displayStyle: theme.displayStyle, displayWeight: theme.displayWeight, displayTracking: theme.displayTracking };
  }
  return { displayStyle: 'normal', displayWeight: '700', displayTracking: '0' };
}

// ---- CSS Generation ----

function buildFullCSS(data: PublicEpkData | EpkFormState): string {
  const c = data.colors;
  const f = data.fonts;
  const tv = getFontThemeVars(data);

  return `
/* ============================================================
   EPK Root — all styles scoped under .epk-root
   ============================================================ */
.epk-root {
  --bg: ${c.background}; --surface: ${c.surface}; --surface-alt: ${c.surfaceAlt};
  --text: ${c.text}; --text-muted: ${c.textMuted}; --accent: ${c.accent}; --accent-2: ${c.accent2};
  --border: ${c.border};
  --font-display: '${f.displayFont}', serif;
  --font-body: '${f.bodyFont}', sans-serif;
  --disp-style: ${tv.displayStyle}; --disp-weight: ${tv.displayWeight}; --disp-tracking: ${tv.displayTracking};
  --space-xs: 8px; --space-sm: 14px; --space-md: 22px; --space-lg: 36px; --space-xl: 56px;
  --radius: 6px;
  --hero-text: ${c.heroText || '#ffffff'};
  --hero-scrim: #000; /* fallback for browsers without relative color syntax */
  background: var(--bg); color: var(--text); font-family: var(--font-body);
  min-height: 100%; line-height: 1.6; -webkit-font-smoothing: antialiased;
}
.epk-root *, .epk-root *::before, .epk-root *::after { box-sizing: border-box; }
.epk-root section { min-width: 0; }
.epk-root a { color: inherit; }
.epk-root h1, .epk-root h2, .epk-root h3, .epk-root .display {
  font-family: var(--font-display); font-style: var(--disp-style);
  font-weight: var(--disp-weight); letter-spacing: var(--disp-tracking); margin: 0;
}
.epk-root p { line-height: 1.65; color: var(--text-muted); margin: 0 0 12px; }
.epk-root p:last-child { margin-bottom: 0; }

@supports (color: lch(from red l c h)) {
  .epk-root {
    /* light text -> black scrim, dark text -> white scrim */
    --hero-scrim: lch(from var(--hero-text) calc((l - 50) * -1000) 0 0);
  }
}

/* Shared */
.epk-root .wrap { padding: var(--space-lg); }
.epk-root .btn {
  display: inline-flex; align-items: center; gap: 6px; padding: 10px 18px;
  border-radius: var(--radius); border: 1px solid var(--border);
  background: var(--surface); color: var(--text); font-family: var(--font-body);
  font-size: 13.5px; font-weight: 500; text-decoration: none; cursor: pointer;
}
.epk-root .btn.primary { background: var(--accent); border-color: var(--accent); color: var(--bg); }
.epk-root h2.section-title { font-size: 22px; margin-bottom: var(--space-md); }

/* Placeholder */
.epk-placeholder {
  background-image: repeating-linear-gradient(135deg, var(--surface-alt), var(--surface-alt) 9px, ${c.surfaceAlt} 9px, ${c.surfaceAlt} 18px);
  border-radius: var(--radius); display: flex; align-items: center; justify-content: center;
  color: var(--text-muted); width: 100%; height: 100%;
}

/* ---- Hero ---- */
.epk-hero .hero-inner { position: relative; width: 100%; min-height: 340px; display: flex; align-items: flex-end; overflow: hidden; border-radius: var(--radius); background: var(--bg); }
.epk-hero .hero-photo { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.epk-hero .hero-scrim {
  position: absolute; inset: 0;
  background: linear-gradient(0deg,
    color-mix(in srgb, var(--hero-scrim) 60%, transparent),
    transparent 60%);
}
.epk-hero .hero-text {
  position: relative; padding: var(--space-lg); color: var(--hero-text);
  text-shadow: 0 1px 2px color-mix(in srgb, var(--hero-scrim) 50%, transparent),
               0 2px 14px color-mix(in srgb, var(--hero-scrim) 45%, transparent);
}
.epk-hero .hero-text h1 { font-size: 44px; color: var(--hero-text); }
.epk-hero .hero-text .tagline { font-size: 15px; font-weight: bold; color: var(--hero-text); margin: 10px 0 18px; max-width: 440px; }
.epk-hero .hero-ctas { display: flex; gap: 10px; flex-wrap: wrap; }
.epk-hero .hero-ctas .btn {
  background: color-mix(in srgb, var(--hero-text) 14%, transparent);
  border-color: color-mix(in srgb, var(--hero-text) 35%, transparent);
  color: var(--hero-text);
  text-shadow: none;
  backdrop-filter: blur(4px);
}
.epk-hero .hero-ctas .btn.primary { background: var(--accent); border-color: var(--accent); color: var(--bg); }

/* ---- Facts ---- */
.epk-facts .facts-inner { display: flex; gap: var(--space-lg); padding: var(--space-md) var(--space-lg); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); flex-wrap: wrap; }
.epk-facts .fact b { display: block; font-size: 15px; margin-bottom: 2px; }
.epk-facts .fact span { font-size: 12px; color: var(--text-muted); }

/* ---- Bio ---- */
.epk-bio .bio-inner { padding: var(--space-lg); }
.epk-bio .bio-lede { font-size: 17px; color: var(--text); margin-bottom: 16px; }

/* ---- Music ---- */
.epk-music .music-inner { padding: var(--space-lg); }
.epk-release-list { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }

.epk-release-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); overflow: visible; }
.epk-release-card .art { aspect-ratio: 1/1; overflow: hidden; border-radius: var(--radius) var(--radius) 0 0; }
.epk-release-card .art img { width: 100%; height: 100%; object-fit: cover; }
.epk-release-card .meta { padding: 12px 14px; }
.epk-release-card .meta b { display: block; font-size: 14.5px; margin-bottom: 2px; }
.epk-release-card .meta span { font-size: 11.5px; color: var(--text-muted); }
.epk-release-card.has-links { cursor: pointer; transition: border-color 0.2s; }
.epk-release-card.has-links:hover { border-color: var(--accent); }
.epk-release-dropdown { position: absolute; left: 0; right: 0; top: 100%; z-index: 10; margin-top: 4px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; box-shadow: 0 4px 16px rgba(0,0,0,.15); }
.epk-release-dropdown-item { display: block; width: 100%; padding: 10px 14px; font-size: 13px; color: var(--text); text-decoration: none; text-transform: capitalize; transition: background 0.15s; cursor: pointer; font-family: var(--font-body); }
.epk-release-dropdown-item:hover { background: var(--surface-alt); }

/* ---- Video ---- */
.epk-video .video-inner { padding: var(--space-lg); }
.epk-video-frame { aspect-ratio: 16/9; border-radius: var(--radius); overflow: hidden; position: relative; background: var(--surface); }
.epk-video-frame iframe { width: 100%; height: 100%; border: none; }
.epk-video-caption { font-size: 15px; color: var(--text-muted); margin-top: 10px; }

/* ---- Gallery ---- */
.epk-gallery .gallery-inner { padding: var(--space-lg); }
.epk-gallery-grid { columns: 3; column-gap: 10px; }
.epk-gallery-grid .tile { aspect-ratio: 1/1; border-radius: var(--radius); overflow: hidden; }
.epk-gallery-grid .tile img { width: 95%; height: 95%; object-fit: contain; }

/* ---- Photo Modal ---- */
.epk-photo-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0, 0, 0, 0.88);
  display: flex; align-items: center; justify-content: center;
  padding: var(--space-lg); cursor: pointer;
}
.epk-photo-modal {
  position: relative; display: flex; flex-direction: column;
  align-items: center; gap: 14px;
  max-width: 90vw; cursor: default;
}
.epk-photo-modal img {
  max-width: 100%; max-height: 75vh;
  object-fit: contain; border-radius: var(--radius);
  display: block;
}
.epk-photo-modal .photo-caption {
  font-size: 14px; color: rgba(255, 255, 255, 0.7);
  text-align: center; max-width: 500px;
  font-family: var(--font-body); margin: 0;
}
.epk-photo-modal .photo-download {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: var(--radius);
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
  font-family: var(--font-body); font-size: 13px; font-weight: 500;
  cursor: pointer; text-decoration: none;
  backdrop-filter: blur(4px);
  transition: background 0.2s, color 0.2s;
}
.epk-photo-modal .photo-download:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}
.epk-photo-modal .photo-download:disabled {
  opacity: 0.4; cursor: default;
}
.epk-photo-modal .photo-download svg { flex-shrink: 0; }
.epk-photo-modal .close-btn {
  position: absolute; top: -44px; right: -4px;
  background: none; border: none;
  color: rgba(255, 255, 255, 0.6); font-size: 28px;
  cursor: pointer; line-height: 1;
  transition: color 0.2s;
}
.epk-photo-modal .close-btn:hover { color: #fff; }

/* ---- Press ---- */
.epk-press .press-inner { padding: var(--space-lg); }
.epk-quote-list { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.epk-quote-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 18px; display: flex; flex-direction: column; }
.epk-quote-card q { display: block; font-family: var(--font-display); font-style: var(--disp-style); font-size: 17px; line-height: 1.4; margin: 0 0 10px; quotes: none; }
.epk-quote-card cite { font-style: normal; font-size: 14px; color: var(--text-muted); margin-top: auto; }

/* ---- Tour ---- */
.epk-tour .tour-inner { padding: var(--space-lg); }
.epk-tour-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 0; border-bottom: 1px solid var(--border); gap: 14px; }
.epk-tour-row:last-child { border-bottom: none; }
.epk-tour-date { font-size: 13px; color: var(--text-muted); width: 110px; flex-shrink: 0; }
.epk-tour-place { flex: 1; min-width: 0; }
.epk-tour-place b { font-size: 15px; display: block; }
.epk-tour-place span { font-size: 12.5px; color: var(--text-muted); }

/* ---- Contact ---- */
.epk-contact .contact-inner { padding: var(--space-lg); background: var(--surface); border-radius: var(--radius); margin: 0 var(--space-lg) var(--space-lg); }
.epk-contact .section-title { margin-bottom: var(--space-md); }
.epk-contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.epk-contact-block b { display: block; font-size: 13px; margin-bottom: 4px; text-transform: capitalize; }
.epk-contact-block span, .epk-contact-block a { display: block; font-size: 13px; color: var(--text-muted); text-decoration: none; margin-bottom: 2px; }
.epk-social-row { display: flex; justify-content: center; gap: 16px; margin-top: 36px; flex-wrap: wrap; }
.epk-social-row a { color: var(--text-muted); text-decoration: none; transition: color 0.2s; display: flex; align-items: center; }
.epk-social-row a:hover { color: var(--accent); }

/* ---- Footer ---- */
.epk-footer { text-align: center; padding: var(--space-lg) 0; font-size: 1.0rem; color: var(--text-muted); }
.epk-footer a { color: var(--accent); text-decoration: none; }
.epk-footer a:hover { text-decoration: underline; }

/* ============================================================
   LAYOUT: headline
   ============================================================ */
.epk-root[data-layout="headline"] { display: flex; flex-direction: column; }
.epk-root[data-layout="headline"] .epk-hero .hero-inner { border-radius: 0; min-height: 420px; }
.epk-root[data-layout="headline"] .bio-inner,
.epk-root[data-layout="headline"] .music-inner,
.epk-root[data-layout="headline"] .video-inner,
.epk-root[data-layout="headline"] .gallery-inner,
.epk-root[data-layout="headline"] .press-inner,
.epk-root[data-layout="headline"] .tour-inner,
.epk-root[data-layout="headline"] .facts-inner { max-width: 820px; margin-left: auto; margin-right: auto; }
.epk-root[data-layout="headline"] .contact-inner { max-width: 820px; margin-left: auto; margin-right: auto; }

/* ============================================================
   LAYOUT: dossier
   ============================================================ */
.epk-root[data-layout="dossier"] { display: grid; grid-template-columns: 300px 1fr; column-gap: 0; }
.epk-root[data-layout="dossier"] .epk-hero { grid-column: 1; grid-row: 1; padding: var(--space-lg) var(--space-lg) 0; }
.epk-root[data-layout="dossier"] .epk-hero .hero-inner { min-height: 0; aspect-ratio: 3/4; align-items: stretch; }
.epk-root[data-layout="dossier"] .epk-hero .hero-text { align-self: flex-end; padding: var(--space-md); }
.epk-root[data-layout="dossier"] .epk-hero .hero-text h1 { font-size: 28px; }
.epk-root[data-layout="dossier"] .epk-facts { grid-column: 1; grid-row: 2; }
.epk-root[data-layout="dossier"] .epk-facts .facts-inner { flex-direction: column; gap: 14px; border: none; padding: var(--space-md) var(--space-lg); }
.epk-root[data-layout="dossier"] .epk-contact { grid-column: 1; grid-row: 3; align-self: start; }
.epk-root[data-layout="dossier"] .epk-contact .contact-inner { margin: var(--space-lg); background: transparent; border: 1px solid var(--border); }
.epk-root[data-layout="dossier"] .epk-contact-grid { grid-template-columns: 1fr; }
.epk-root[data-layout="dossier"] .epk-bio { grid-column: 2; grid-row: 1; }
.epk-root[data-layout="dossier"] .epk-music { grid-column: 2; grid-row: 2; }
.epk-root[data-layout="dossier"] .epk-video { grid-column: 2; grid-row: 3; }
.epk-root[data-layout="dossier"] .epk-gallery { grid-column: 2; grid-row: 4; }
.epk-root[data-layout="dossier"] .epk-press { grid-column: 2; grid-row: 5; }
.epk-root[data-layout="dossier"] .epk-tour { grid-column: 2; grid-row: 6; }
.epk-root[data-layout="dossier"] .epk-quote-list { grid-template-columns: 1fr; }
.epk-root[data-layout="dossier"] .epk-footer { grid-column: 1 / -1; grid-row: 99; }

/* ============================================================
   LAYOUT: bulletin
   ============================================================ */
.epk-root[data-layout="bulletin"] { display: grid; grid-template-columns: repeat(12, 1fr); column-gap: var(--space-md); row-gap: var(--space-md); padding: var(--space-md); }
.epk-root[data-layout="bulletin"] .epk-hero { grid-column: 1/13; grid-row: 1; }
.epk-root[data-layout="bulletin"] .epk-facts { grid-column: 1/13; grid-row: 2; }
.epk-root[data-layout="bulletin"] .epk-bio { grid-column: 1/7; grid-row: 3; }
.epk-root[data-layout="bulletin"] .epk-music { grid-column: 7/13; grid-row: 3; }
.epk-root[data-layout="bulletin"] .epk-gallery { grid-column: 1/13; grid-row: 4; }
.epk-root[data-layout="bulletin"] .epk-video { grid-column: 1/6; grid-row: 5; }
.epk-root[data-layout="bulletin"] .epk-press { grid-column: 6/13; grid-row: 5; }
.epk-root[data-layout="bulletin"] .epk-tour { grid-column: 1/8; grid-row: 6; }
.epk-root[data-layout="bulletin"] .epk-contact { grid-column: 8/13; grid-row: 6; }
.epk-root[data-layout="bulletin"] .epk-contact .contact-inner { margin: 0; height: 100%; }
.epk-root[data-layout="bulletin"] .epk-contact-grid { grid-template-columns: 1fr; }
.epk-root[data-layout="bulletin"] .epk-release-list { grid-template-columns: 1fr; }
.epk-root[data-layout="bulletin"] .epk-quote-list { grid-template-columns: 1fr; }
.epk-root[data-layout="bulletin"] .epk-gallery-grid { columns: 4; }
.epk-root[data-layout="bulletin"] .epk-footer { grid-column: 1 / -1; grid-row: 99; }

/* ============================================================
   LAYOUT: reel
   ============================================================ */
.epk-root[data-layout="reel"] { display: grid; grid-template-columns: 1fr 1fr; }
.epk-root[data-layout="reel"] .epk-hero { grid-column: 1; grid-row: 1; }
.epk-root[data-layout="reel"] .epk-hero .hero-inner { min-height: 0; aspect-ratio: 3/4; align-items: stretch; }
.epk-root[data-layout="reel"] .epk-hero .hero-text { align-self: flex-end; padding: var(--space-md); }
.epk-root[data-layout="reel"] .epk-hero .hero-text h1 { font-size: 28px; }
.epk-root[data-layout="reel"] .epk-hero .hero-ctas { flex-wrap: wrap; }
.epk-root[data-layout="reel"] .epk-facts { grid-column: 2; grid-row: 1; display: flex; align-items: center; }
.epk-root[data-layout="reel"] .epk-facts .facts-inner { flex-direction: column; align-items: flex-start; justify-content: center; border: none; gap: 16px; }
.epk-root[data-layout="reel"] .epk-bio { grid-column: 1/3; grid-row: 2; }
.epk-root[data-layout="reel"] .epk-video { grid-column: 1/3; grid-row: 3; }
.epk-root[data-layout="reel"] .epk-music { grid-column: 1/3; grid-row: 4; }
.epk-root[data-layout="reel"] .epk-gallery { grid-column: 1/3; grid-row: 5; }
.epk-root[data-layout="reel"] .epk-press { grid-column: 1/3; grid-row: 6; }
.epk-root[data-layout="reel"] .epk-tour { grid-column: 1/3; grid-row: 7; }
.epk-root[data-layout="reel"] .epk-contact { grid-column: 1/3; grid-row: 8; }
.epk-root[data-layout="reel"] .epk-footer { grid-column: 1 / -1; grid-row: 99; }

/* ============================================================
   MOBILE — collapse any layout to one column
   ============================================================ */
@media (max-width: 768px) {
  .epk-root { display: flex !important; flex-direction: column !important; padding: 0 !important; }
  .epk-root > section { grid-column: auto !important; grid-row: auto !important; }
  .epk-root .epk-hero .hero-inner { border-radius: 0 !important; aspect-ratio: auto !important; min-height: 300px !important; align-items: flex-end !important; }
  .epk-root .epk-hero .hero-text h1 { font-size: 32px !important; }
  .epk-root .epk-facts .facts-inner { flex-direction: column !important; gap: 14px !important; }
  .epk-root .epk-release-list { grid-template-columns: 1fr 1fr !important; }
  .epk-root .epk-quote-list { grid-template-columns: 1fr !important; }
  .epk-root .epk-gallery-grid { columns: 2 !important; }
  .epk-root .epk-contact-grid { grid-template-columns: 1fr !important; }
  .epk-root .epk-contact .contact-inner { margin: 0 var(--space-md) var(--space-md) !important; }
  .epk-root .wrap, .epk-root .bio-inner, .epk-root .music-inner, .epk-root .video-inner,
  .epk-root .gallery-inner, .epk-root .press-inner, .epk-root .tour-inner { padding: var(--space-md) !important; }
  .epk-root .epk-hero { padding: 0 !important; }
  .epk-root .epk-tour-row { flex-wrap: wrap; }
}

/* ============================================================
   MOBILE PREVIEW — mirrors @media rules for builder preview
   ============================================================ */
.epk-root[data-preview-mobile="true"] { display: flex !important; flex-direction: column !important; padding: 0 !important; overflow-x: hidden; width: 100%; max-width: 100%; }
.epk-root[data-preview-mobile="true"] > section { grid-column: auto !important; grid-row: auto !important; }
.epk-root[data-preview-mobile="true"] .epk-hero .hero-inner { border-radius: 0 !important; aspect-ratio: auto !important; min-height: 300px !important; align-items: flex-end !important; }
.epk-root[data-preview-mobile="true"] .epk-hero .hero-text h1 { font-size: 32px !important; }
.epk-root[data-preview-mobile="true"] .epk-facts .facts-inner { flex-direction: column !important; gap: 14px !important; }
.epk-root[data-preview-mobile="true"] .epk-release-list { grid-template-columns: 1fr 1fr !important; }
.epk-root[data-preview-mobile="true"] .epk-quote-list { grid-template-columns: 1fr !important; }
.epk-root[data-preview-mobile="true"] .epk-gallery-grid { columns: 2 !important; }
.epk-root[data-preview-mobile="true"] .epk-contact-grid { grid-template-columns: 1fr !important; }
.epk-root[data-preview-mobile="true"] .epk-contact .contact-inner { margin: 0 var(--space-md) var(--space-md) !important; }
.epk-root[data-preview-mobile="true"] .wrap,
.epk-root[data-preview-mobile="true"] .bio-inner,
.epk-root[data-preview-mobile="true"] .music-inner,
.epk-root[data-preview-mobile="true"] .video-inner,
.epk-root[data-preview-mobile="true"] .gallery-inner,
.epk-root[data-preview-mobile="true"] .press-inner,
.epk-root[data-preview-mobile="true"] .tour-inner { padding: var(--space-md) !important; }
.epk-root[data-preview-mobile="true"] .epk-hero { padding: 0 !important; }
.epk-root[data-preview-mobile="true"] .epk-tour-row { flex-wrap: wrap; }
.epk-root[data-preview-mobile="true"] .epk-footer { grid-column: auto !important; grid-row: auto !important; }
.epk-root[data-preview-mobile="true"] * { overflow-wrap: break-word; word-wrap: break-word; }
`;
}

// ============================================================
// React Component
// ============================================================

export default function EpkRenderer({
  data,
  onLinkClick,
  isPreview = false,
  previewMode,
}: EpkRendererProps) {
  const fontsUrl = buildGoogleFontsUrl(data.fonts.displayFont, data.fonts.bodyFont);
  const fullCSS = buildFullCSS(data);
  const [openReleaseId, setOpenReleaseId] = useState<string | null>(null);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);

  // ---- Section renderers ----

  useEffect(() => {
    if (!openReleaseId) return;
    const handleClickOutside = () => setOpenReleaseId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openReleaseId]);

  useEffect(() => {
    if (!selectedPhotoId) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedPhotoId(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedPhotoId]);

  const renderHero = () => (
    <section className="epk-hero">
      <div className="hero-inner">
        {data.hero_image_url ? (
          <img
            src={data.hero_image_url}
            alt=""
            className="hero-photo"
            style={{
              objectFit: data.hero_image_fit === 'contain' ? 'contain' : 'cover',
              objectPosition: data.hero_image_position || 'center',
            }}
          />
        ) : (
          <div className="hero-photo epk-placeholder" />
        )}
        <div className="hero-scrim" />
        <div className="hero-text">
          {(data.show_hero_artist_name !== false) && (
            <h1>{data.artist_name || 'Artist Name'}</h1>
          )}
          {data.tagline && <p className="tagline">{data.tagline}</p>}
          <div className="hero-ctas">
            {data.listen_url && (
              <a
                className="btn primary"
                href={data.listen_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (isPreview) e.preventDefault();
                  else onLinkClick?.('listen', 'Listen now');
                }}
              >
                Listen now
              </a>
            )}
            {data.booking_url && (
              <a
                className="btn"
                href={data.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (isPreview) e.preventDefault();
                  else onLinkClick?.('booking', 'Booking inquiries');
                }}
              >
                Booking inquiries
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );

  const renderFacts = () => {
    if (!data.show_facts) return null;
    if (!data.genre && !data.location && !data.for_fans_of) return null;
    return (
      <section className="epk-facts">
        <div className="facts-inner">
          {data.genre && <div className="fact"><span>Genre</span><b>{data.genre}</b></div>}
          {data.location && <div className="fact"><span>Based in</span><b>{data.location}</b></div>}
          {data.for_fans_of && <div className="fact"><span>For fans of</span><b>{data.for_fans_of}</b></div>}
        </div>
      </section>
    );
  };

  const renderBio = () => {
    if (!data.show_bio || !data.bio) return null;
    const paragraphs = data.bio.split('\n').filter((p) => p.trim());
    return (
      <section className="epk-bio">
        <div className="bio-inner">
          <h2 className="section-title">Biography</h2>
          {paragraphs.map((p, i) => (
            <p key={i} className={i === 0 ? 'bio-lede' : ''}>{p}</p>
          ))}
        </div>
      </section>
    );
  };

  const renderMusic = () => {
    if (!data.show_music || !data.releases || data.releases.length === 0) return null;
    return (
      <section className="epk-music">
        <div className="music-inner">
          <h2 className="section-title">Music</h2>
          <div className="epk-release-list">
            {data.releases.map((release) => (
              <div
                key={release.release_id}
                className={`epk-release-card ${release.links.length > 0 ? 'has-links' : ''}`}
                style={{ position: 'relative', cursor: release.links.length > 0 ? 'pointer' : 'default' }}


                onClick={(e) => {
                  e.stopPropagation();
                  if (release.links.length > 0) {
                    setOpenReleaseId(openReleaseId === release.release_id ? null : release.release_id);
                  }
                }}
              >
                <div className="art">
                  {release.cover_image_url ? (
                    <img src={release.cover_image_url} alt={release.title} />
                  ) : (
                    <div className="epk-placeholder" />
                  )}
                </div>
                <div className="meta">
                  <b>{release.title}</b>
                  <span>{release.release_type.toUpperCase()} · {release.year}</span>
                </div>
                {openReleaseId === release.release_id && release.links.length > 0 && (
                  <div className="epk-release-dropdown" onClick={(e) => e.stopPropagation()}>
                    {release.links.map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="epk-release-dropdown-item"
                        onClick={(e) => {
                          if (isPreview) {
                            e.preventDefault();
                          } else {
                            onLinkClick?.(release.release_id, `${release.title} - ${link.platform}`);
                          }
                        }}
                      >
                        {link.platform.replace('-', ' ')}
                      </a>
                    ))}
                  </div>
                )}
              </div>              
            ))}
          </div>
        </div>
      </section>
    );
  };

  const renderVideo = () => {
    if (!data.show_video || !data.video_url) return null;
    // Convert YouTube/Vimeo URLs to embed URLs
    let embedUrl = data.video_url;
    if (embedUrl.includes('youtube.com/watch')) {
      const id = new URL(embedUrl).searchParams.get('v');
      if (id) embedUrl = `https://www.youtube.com/embed/${id}`;
    } else if (embedUrl.includes('youtu.be/')) {
      const id = embedUrl.split('youtu.be/')[1]?.split('?')[0];
      if (id) embedUrl = `https://www.youtube.com/embed/${id}`;
    } else if (embedUrl.includes('vimeo.com/') && !embedUrl.includes('player.vimeo.com')) {
      const id = embedUrl.split('vimeo.com/')[1]?.split('?')[0];
      if (id) embedUrl = `https://player.vimeo.com/video/${id}`;
    }
    return (
      <section className="epk-video">
        <div className="video-inner">
          <h2 className="section-title">Video</h2>
          <div className="epk-video-frame">
            {isPreview ? (
              <div className="epk-placeholder" style={{ aspectRatio: '16/9' }}>▶ Video Preview</div>
            ) : (
              <iframe src={embedUrl} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Video" />
            )}
          </div>
          {data.video_caption && <p className="epk-video-caption">{data.video_caption}</p>}
        </div>
      </section>
    );
  };

  const renderGallery = () => {
    if (!data.show_gallery || !data.gallery_images || data.gallery_images.length === 0) return null;
    return (
      <section className="epk-gallery">
        <div className="gallery-inner">
          <h2 className="section-title">Photos</h2>
          <div className="epk-gallery-grid">
            {data.gallery_images.map((img) => (
              <div
                key={img.image_id}
                className="tile"
                onClick={() => img.image_url && setSelectedPhotoId(img.image_id)}
              >
                {img.image_url ? (
                  <img src={img.image_url} alt={img.caption || ''} />
                ) : (
                  <div className="epk-placeholder" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const handlePhotoDownload = async (imageUrl: string, caption: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      // Derive a filename from the URL or use a fallback
      const urlPath = new URL(imageUrl).pathname;
      const ext = urlPath.split('.').pop() || 'jpg';
      const name = caption
        ? caption.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 40)
        : 'photo';
      a.download = `${name}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // Fallback: open image in new tab
      window.open(imageUrl, '_blank');
    }
  };

  const renderPhotoModal = () => {
    if (!selectedPhotoId) return null;
    const photo = data.gallery_images?.find((img) => img.image_id === selectedPhotoId);
    if (!photo || !photo.image_url) return null;

    return (
      <div
        className="epk-photo-overlay"
        onClick={() => setSelectedPhotoId(null)}
      >
        <div className="epk-photo-modal" onClick={(e) => e.stopPropagation()}>
          <button
            className="close-btn"
            onClick={() => setSelectedPhotoId(null)}
            aria-label="Close"
          >
            ✕
          </button>
          <img src={photo.image_url} alt={photo.caption || ''} />
          {photo.caption && (
            <p className="photo-caption">{photo.caption}</p>
          )}
          <button
            className="photo-download"
            disabled={isPreview}
            title={isPreview ? 'Download disabled in preview' : 'Download photo'}
            onClick={() => {
              if (!isPreview && photo.image_url) {
                handlePhotoDownload(photo.image_url, photo.caption || '');
              }
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Download
          </button>
        </div>
      </div>
    );
  };

  const renderPress = () => {
    if (!data.show_press || !data.press_quotes || data.press_quotes.length === 0) return null;
    return (
      <section className="epk-press">
        <div className="press-inner">
          <h2 className="section-title">Press</h2>
          <div className="epk-quote-list">
            {data.press_quotes.map((pq) => (
              <div key={pq.quote_id} className="epk-quote-card">
                <q>{pq.quote}</q>
                <cite>
                  {pq.url ? (
                    <a href={pq.url} target="_blank" rel="noopener noreferrer" onClick={(e) => isPreview && e.preventDefault()}>
                      {pq.source}
                    </a>
                  ) : (
                    pq.source
                  )}
                </cite>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const renderTour = () => {
    if (!data.show_tour || !data.tour_dates || data.tour_dates.length === 0) return null;
    return (
      <section className="epk-tour">
        <div className="tour-inner">
          <h2 className="section-title">Recent Performances</h2>
          {data.tour_dates.map((td) => (
            <div key={td.tour_date_id} className="epk-tour-row">
              <span className="epk-tour-date">{formatTourDate(td.date)}</span>
              <span className="epk-tour-place">
                <b>{td.venue}</b>
                <span>{td.city}</span>
              </span>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderContact = () => {
    if (!data.show_contact) return null;
    if ((!data.contacts || data.contacts.length === 0) && (!data.social_links || data.social_links.length === 0)) return null;
    return (
      <section className="epk-contact">
        <div className="contact-inner">
          <h2 className="section-title">Contact</h2>
          {data.contacts && data.contacts.length > 0 && (
            <div className="epk-contact-grid">
              {data.contacts.map((c) => (
                <div key={c.contact_id} className="epk-contact-block">
                  <b>{c.role}</b>
                  {c.name && <span>{c.name}</span>}
                  <a href={`mailto:${c.email}`} onClick={(e) => isPreview && e.preventDefault()}>{c.email}</a>
                </div>
              ))}
            </div>
          )}
          {data.social_links && data.social_links.length > 0 && (
            <div className="epk-social-row">
              {data.social_links.map((sl) => (
                <a
                  key={sl.social_link_id}
                  href={sl.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={sl.platform.replace('-', ' ')}
                  onClick={(e) => {
                    if (isPreview) e.preventDefault();
                    else onLinkClick?.(sl.social_link_id, sl.platform);
                  }}
                >
                  {SOCIAL_ICON_SVGS[sl.platform] || SOCIAL_ICON_SVGS['other']}
                </a>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  };

  const renderFooter = () => (
    <footer className="epk-footer">
      <a href="https://www.bandjerry.com" target="_blank" rel="noopener noreferrer" onClick={(e) => isPreview && e.preventDefault()}>
        Powered by BandJerry
      </a>
    </footer>
  );

  return (
    <div className="epk-root" data-layout={data.layout} {...(previewMode === 'mobile' ? { 'data-preview-mobile': 'true' } : {})}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href={fontsUrl} rel="stylesheet" />
      <style dangerouslySetInnerHTML={{ __html: fullCSS }} />
      {renderHero()}
      {renderFacts()}
      {renderBio()}
      {renderVideo()}
      {renderMusic()}
      {renderGallery()}
      {renderPress()}
      {renderTour()}
      {renderContact()}
      {renderFooter()}
      {renderPhotoModal()}
    </div>
  );
}
